import { cn } from "@d12labs/utils";
import { categoryScoreColor, overallScoreColor } from "../types";

const VALUE_SIZE = {
	sm: "text-base",
	md: "text-[22px]",
	lg: "text-4xl",
	xl: "text-[56px]",
} as const;
const OUT_SIZE = { sm: "text-meta", md: "text-xs", lg: "text-sm", xl: "text-base" } as const;

/**
 * The single, reusable way to render a score with its color semantics: green for
 * strong (≥80 / ≥8), neutral ink for competent, amber for needs-improvement.
 * `kind` switches between the 0–100 composite and a 0–10 category.
 */
export function ScoreBadge({
	value,
	kind = "overall",
	size = "md",
	showOutOf = false,
}: {
	value: number;
	kind?: "overall" | "category";
	size?: keyof typeof VALUE_SIZE;
	showOutOf?: boolean;
}) {
	const color = kind === "overall" ? overallScoreColor(value) : categoryScoreColor(value);
	const max = kind === "overall" ? 100 : 10;
	return (
		<span
			className={cn(
				"inline-flex items-baseline gap-0.5 font-display font-semibold leading-none tracking-tight tabular-nums",
				VALUE_SIZE[size],
				color,
			)}
		>
			{value}
			{showOutOf ? (
				<span className={cn("font-medium text-ink-tertiary tracking-normal", OUT_SIZE[size])}>
					/{max}
				</span>
			) : null}
		</span>
	);
}

/** Overall score in a hairline-thin donut ring; stroke turns green at ≥80. */
export function ScoreRing({
	value,
	size = 96,
	stroke = 4,
}: {
	value: number;
	size?: number;
	stroke?: number;
}) {
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const pct = Math.max(0, Math.min(100, value)) / 100;
	// Auto-fit the centered number: a 2-digit score ("78") can run large, but
	// decimals ("60.75") or "100" need to shrink so they don't crowd the arc.
	const label = String(value);
	const fontScale =
		label.length >= 5 ? 0.22 : label.length === 4 ? 0.26 : label.length === 3 ? 0.3 : 0.36;
	const color =
		value >= 80
			? "var(--color-semantic-success)"
			: value >= 60
				? "var(--color-foreground)"
				: "var(--color-chart-3)";
	return (
		<div
			className="relative inline-flex items-center justify-center"
			style={{ width: size, height: size }}
		>
			<svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
				<circle
					cx={size / 2}
					cy={size / 2}
					r={r}
					stroke="var(--color-hairline)"
					strokeWidth={stroke}
					fill="none"
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={r}
					stroke={color}
					strokeWidth={stroke}
					fill="none"
					strokeDasharray={`${c * pct} ${c}`}
					strokeLinecap="round"
					style={{ transition: "stroke-dasharray 600ms ease" }}
				/>
			</svg>
			<div className="absolute inset-0 flex flex-col items-center justify-center">
				<span
					className={cn(
						"font-display font-semibold leading-none tracking-tight tabular-nums",
						overallScoreColor(value),
					)}
					style={{ fontSize: Math.round(size * fontScale) }}
				>
					{label}
				</span>
				<span className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-tertiary">/100</span>
			</div>
		</div>
	);
}
